import FileContentUploaderButton from 'components/FileContentUploaderButton/ContentUploader';
import FileDownloadDropdown from 'components/FileDownloadDropdown/FileDownloadDropdown';
import IconButton from 'components/IconButton';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  showFailureToast,
  showSuccessToast,
  showWarningToast,
} from 'lib/ToastHelper';
import { Suspense, useCallback, useEffect } from 'react';
import { Col, Badge } from 'reactstrap';
import { companyIdAtom } from 'views/state';

import { appliedCAIdsAtom, caRefreshAtom } from '../state';
import {
  caListingDataAtom,
  checkedListAtom,
  vulnCountForCheckedHostsAtom,
  forceRerenderUIAtom,
  checkedHostsVulnMapAtom,
  caDataQueryParamsAtom,
  useCAListFilterValue,
} from './state';
const DashboardActions = () => {
  const companyId = useAtomValue(companyIdAtom);
  const caDataQueryParams = useAtomValue(caDataQueryParamsAtom);
  const setCaRefresh = useSetAtom(caRefreshAtom);
  const listingData = useAtomValue(caListingDataAtom);
  const [selectedRowIds, setSelectedRowIds] = useAtom(checkedListAtom);
  const [appliedRowIds, setAppliedRowIds] = useAtom(appliedCAIdsAtom);
  const [vulnMap, setVulnMap] = useAtom(checkedHostsVulnMapAtom);
  const forceRerenderUI = useSetAtom(forceRerenderUIAtom);
  const vulnCountForCheckedHosts = useAtomValue(vulnCountForCheckedHostsAtom);

  const selectionChanged = (): boolean => {
    return appliedRowIds.sort().join(',') !== selectedRowIds.sort().join(',');
  };

  const markAppliedRows = useCallback(
    (rowIds) => {
      if (listingData) {
        // TODO: update the applied row background color to indicate the applied rows
        for (let i = 0; i < listingData.items?.length; i++) {
          const row = listingData.items[i];
          row['isAppliedForDashboard'] = false;
        }
        const effectedRows = listingData?.items?.filter((row) =>
          rowIds.includes(row.id),
        );
        if (effectedRows?.length > 0) {
          effectedRows.forEach((row) => {
            row['isAppliedForDashboard'] = true;
          });
        }
        forceRerenderUI(Math.random().toString());
      }
    },
    [forceRerenderUI, listingData],
  );

  useEffect(() => {
    markAppliedRows(appliedRowIds);
  }, [appliedRowIds, listingData, markAppliedRows]);

  useEffect(() => {
    if (listingData?.items) {
      const newVulnMap = { ...vulnMap };

      // Remove vulnerability counts for unchecked hosts
      Object.keys(newVulnMap).forEach((id) => {
        if (!selectedRowIds.includes(Number(id))) {
          delete newVulnMap[id];
        }
      });

      // Add vulnerability counts for newly checked hosts
      listingData.items.forEach((host) => {
        if (selectedRowIds.includes(host.id)) {
          newVulnMap[host.id] = host.total_vuln || 0;
        }
      });

      setVulnMap(newVulnMap);
    }
  }, [selectedRowIds, listingData, setVulnMap]);

  const onSuccessBulkAlias = (response) => {
    setCaRefresh(Math.random().toString());
    if (
      response['cloud_agent_alias_csv'] ===
      response['cloud_agent_alias_updated']
    )
      showSuccessToast(response['message']);
    else showWarningToast(response['message']);
  };

  const onUpdateDashboardClick = () => {
    if (vulnCountForCheckedHosts > 0 || selectedRowIds?.length === 0) {
      markAppliedRows(selectedRowIds);
      setAppliedRowIds(selectedRowIds);
    } else {
      showWarningToast(
        'Dashboard can not be applied, as for the selected hosts, the vulnerabilities/bugs total count is zero. Please try a different combination of hosts.',
      );
    }
  };
  const selectedCloudAgentsFilter = useCAListFilterValue<
    'active' | 'aging' | 'deleted'
  >('filter');

  // Keep CSV export aligned with the Cloud Agents grid by reusing the same
  // query params; selected rows go in the body to avoid very long query strings.
  const getExportUrlParams = (csvType?: string) => ({
    ...(caDataQueryParams || {}),
    ...(csvType ? { csv_type: csvType } : {}),
  });

  const getExportRequestData = () =>
    selectedRowIds?.length > 0 ? { host_id_list: selectedRowIds } : null;

  return (
    <Suspense fallback={<></>}>
      <Col
        className="text-right"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          width: '100%',
        }}
      >
        {selectedRowIds?.length > 0 && (
          <Col
            md="auto"
            className="text-left text-nowrap"
            style={{ marginRight: 'auto' }}
          >
            {selectedRowIds?.length > 0 && (
              <>
                ( Selected:{' '}
                <label
                  className="label btn"
                  style={{
                    backgroundColor: 'rgba(7, 114, 219, 0.1)',
                    color: 'black',
                  }}
                >
                  {selectedRowIds.length}
                </label>
                )
              </>
            )}
          </Col>
        )}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {selectedRowIds?.length > 0 && (
            <>
              <IconButton
                id="clearSelectionButton"
                iconClass="fas fa-times"
                tooltip="Clear all selections"
                onClick={() => setSelectedRowIds([])}
              />
              <div
                style={{
                  width: '1px',
                  height: '24px',
                  backgroundColor: '#dee2e6',
                  margin: '0 5px',
                }}
              />
            </>
          )}
          <IconButton
            id="updateButton"
            iconClass="fa fa-chart-line fa-lg"
            tooltip="Update Dashboard For Selected Hosts"
            disabled={
              !selectionChanged() ||
              (selectedRowIds.length === 0 && appliedRowIds.length === 0)
            }
            active={appliedRowIds.length > 0}
            stale={selectionChanged()}
            onClick={onUpdateDashboardClick}
          />
          <div style={{ position: 'relative' }} className="d-inline">
            <IconButton
              id="resetButton"
              iconClass="fa fa-undo fa-lg"
              tooltip={
                appliedRowIds?.length > 0 ? (
                  <>
                    Reset dashboard to show all hosts
                    <br />
                    Applied ( {appliedRowIds.length} hosts )
                  </>
                ) : (
                  'Reset dashboard to show all hosts'
                )
              }
              disabled={appliedRowIds.length === 0}
              onClick={() => {
                markAppliedRows([]);
                setAppliedRowIds([]);
              }}
            />
            {appliedRowIds?.length > 0 && (
              <Badge
                color="transparent"
                pill
                style={{
                  position: 'absolute',
                  top: '-7px',
                  right: '-6px',
                  fontSize: '0.70rem',
                  color: '#fff',
                  textShadow:
                    '-1px -1px 0 black, 1px -1px 0 black, -1px 1px 0 black, 1px 1px 0 black',

                  cursor: 'pointer',
                }}
              >
                {appliedRowIds.length}
              </Badge>
            )}
          </div>
          <FileDownloadDropdown
            buttonId="ca_export_all"
            iconClass="fa fa-file-export"
            tooltip="Export CSV data for Cloud Agents"
            method={selectedRowIds?.length > 0 ? 'POST' : 'GET'}
            data={getExportRequestData()}
            config={[
              {
                url: 'cloud_agents/export-csv',
                label: 'Export Cloud Agents',
                fileName: `${localStorage
                  .getItem('currentCompany')
                  ?.toLowerCase()}_cloud_agents.csv`,
                urlParams: getExportUrlParams(),
              },
              {
                url: 'cloud_agents/export-csv',
                label: 'Export Aliases',
                fileName: `${localStorage
                  .getItem('currentCompany')
                  ?.toLowerCase()}_cloud_agent_aliases.csv`,
                urlParams: getExportUrlParams('alias'),
              },
              {
                url: 'cloud_agents/export-detail-csv',
                label: 'Export Installed Software',
                fileName: `${localStorage
                  .getItem('currentCompany')
                  ?.toLowerCase()}_cloud_agent_softwares.csv`,
                urlParams: getExportUrlParams('ca_softwares'),
              },
              {
                url: 'cloud_agents/export-detail-csv',
                label: 'Export Open Ports',
                fileName: `${localStorage
                  .getItem('currentCompany')
                  ?.toLowerCase()}_cloud_agent_open_ports.csv`,
                urlParams: getExportUrlParams('ca_ports'),
              },
            ]}
            onSuccessCallback={() =>
              showSuccessToast('File download completed.')
            }
            onFailureCallback={(message) => showFailureToast(message)}
          />
          <FileContentUploaderButton
            buttonId="bulkaliasButton"
            iconClass="fa fa-file-import"
            tooltip="Import/Update Cloud Agent Alias In Bulk"
            method="POST"
            uploadUrl="cloud_agents/alias/bulk/"
            allowedExtensions={['csv']}
            data={{ company_id: companyId }}
            onSuccessCallback={onSuccessBulkAlias}
            onFailureCallback={(message) => showFailureToast(message)}
            disabled={selectedCloudAgentsFilter === 'deleted'}
          />
        </div>
      </Col>
    </Suspense>
  );
};

export default DashboardActions;
